/**
 * Purchasing quantities (TASK-0228) and the order guide's pack, rounding, cost
 * and phone helpers (TASK-0229, further down).
 *
 * One reader for every purchase quantity an operator types, so the console and
 * the API agree on what a quantity box may hold. The rule it enforces is "reject,
 * never reinterpret": text that is not a clean quantity for its unit reads as
 * nothing (`null`), it is never coerced. Two coercions caused real risk before
 * this file existed:
 *
 * - `Number("")` is 0, so clearing a box silently became a quantity of 0;
 * - a box whose shown text was rebuilt from `Number(text)` on every keystroke
 *   dropped the decimal point ("0." became "0"), so typing 0.5 lb landed as 5 lb.
 *
 * Units:
 * - `ea` and `pack` take whole numbers only (digits, nothing else);
 * - `lb` takes up to 3 decimals: `purchase_order_items.ordered_qty` and
 *   `received_qty` are numeric(12,3). A leading or trailing point is accepted
 *   (".5" is 0.5, "5." is 5) because neither can mean anything else.
 *
 * Every unit is capped at 9 whole digits, the integer part numeric(12,3) holds.
 * Signs, exponents, thousands separators and fractions ("1/2") are refused.
 * Zero is a valid reading; whether a zero is allowed is the caller's rule (a
 * cumulative receive may be 0, an ordered line may not).
 */
import { cents } from "./money.js";

export type QuantityUnit = "ea" | "pack" | "lb";

/** The most decimals each unit takes. */
export const QUANTITY_DECIMALS: Readonly<Record<QuantityUnit, number>> = {
  ea: 0,
  pack: 0,
  lb: 3,
};

/** numeric(12,3) keeps 9 digits before the point. */
export const QUANTITY_MAX_WHOLE_DIGITS = 9;

export type QuantityProblem =
  /** Nothing typed (or only spaces). */
  | "blank"
  /** Not a plain non-negative decimal: letters, signs, commas, a lone ".". */
  | "not_a_number"
  /** A decimal point in an `ea` or `pack` box. */
  | "not_whole"
  /** More than 3 decimals in an `lb` box. */
  | "too_many_decimals"
  /** More whole digits than numeric(12,3) holds. */
  | "too_large";

export type QuantityReading = { ok: true; value: number } | { ok: false; problem: QuantityProblem };

const DECIMAL_TEXT = /^(\d*)(?:\.(\d*))?$/;

/** Read typed quantity text for a unit, saying why when it cannot be read. */
export function readQuantity(text: string, unit: QuantityUnit): QuantityReading {
  const trimmed = text.trim();
  if (trimmed === "") return { ok: false, problem: "blank" };

  const match = DECIMAL_TEXT.exec(trimmed);
  const whole = match?.[1] ?? "";
  const fraction = match?.[2];
  if (!match || (whole === "" && (fraction ?? "") === "")) {
    return { ok: false, problem: "not_a_number" };
  }
  if (fraction !== undefined && QUANTITY_DECIMALS[unit] === 0) {
    return { ok: false, problem: "not_whole" };
  }
  if (fraction !== undefined && fraction.length > QUANTITY_DECIMALS[unit]) {
    return { ok: false, problem: "too_many_decimals" };
  }
  if (whole.replace(/^0+/, "").length > QUANTITY_MAX_WHOLE_DIGITS) {
    return { ok: false, problem: "too_large" };
  }
  // The text is now a plain decimal with at most 3 places, so Number() reads
  // it exactly as typed ("5." → 5, ".5" → 0.5).
  return { ok: true, value: Number(trimmed) };
}

/**
 * The quantity typed in a box, or `null` when the box is blank or the text is
 * not a quantity for this unit. Never 0 for a blank box.
 */
export function parseQuantity(text: string, unit: QuantityUnit): number | null {
  const reading = readQuantity(text, unit);
  return reading.ok ? reading.value : null;
}

/**
 * Text for a quantity box seeded from a number (an API value, a "receive all",
 * an increment). Rounded to 3 decimals so float noise from arithmetic
 * (1.005 + 1) never reaches the box.
 */
export function quantityText(value: number): string {
  if (!Number.isFinite(value)) return "";
  return String(Math.round(value * 1000) / 1000);
}

// ---------------------------------------------------------------------------
// Order guide: packs, rounding, cost and phone helpers (TASK-0229, formula R1,
// docs/specs/WEEKLY-ORDER.md). Shared by the API, which computes suggestions,
// and the console, which re-totals what the buyer types.
//
// Quantities are in the product's own unit: integer eaches for 'ea', pounds
// (3 dp) for 'lb'. A "pack" is the vendor's case: `packSize` eaches per case.
// Money is integer cents.
// ---------------------------------------------------------------------------

/** Float slack for ceil/floor on values that came through decimal arithmetic (0.1 + 0.2). */
export const QTY_EPSILON = 1e-9;

/** Round to the 3 dp a numeric(12,3) quantity column holds. */
export function roundQty(value: number): number {
  if (!Number.isFinite(value)) return 0;
  const rounded = Math.round((Math.abs(value) + Number.EPSILON) * 1000) / 1000;
  return value < 0 ? -rounded : rounded;
}

/** A quantity as the API sends it: a decimal string with at most 3 dp and no trailing zeros. */
export function formatQty(value: number): string {
  const rounded = roundQty(value);
  if (Object.is(rounded, -0) || rounded === 0) return "0";
  return rounded.toFixed(3).replace(/\.?0+$/, "");
}

/** A usable case size: an integer of 2 or more. 1, 0, null and fractions are "no pack". */
export function usablePackSize(packSize: number | null | undefined): number | null {
  if (packSize == null || !Number.isFinite(packSize)) return null;
  return packSize >= 2 && Number.isInteger(packSize) ? packSize : null;
}

export function packsToUnits(packs: number, packSize: number): number {
  return roundQty(packs * packSize);
}

/** Units as cases. Not rounded: 18 eaches of a 12-pack is 1.5 cases. */
export function unitsToPacks(units: number, packSize: number): number {
  return units / packSize;
}

/** Whole cases covering `units`, and the units they hold. Zero or less needs no case. */
export function ceilToPack(units: number, packSize: number): { packs: number; qty: number } {
  if (!(units > 0)) return { packs: 0, qty: 0 };
  const packs = Math.ceil(units / packSize - QTY_EPSILON);
  return { packs, qty: packsToUnits(packs, packSize) };
}

/** The smallest need worth an order line: half a case, or half a unit when there is no case. */
export function halfPackThreshold(packSize: number | null | undefined): number {
  const k = usablePackSize(packSize);
  return k ? 0.5 * k : 0.5;
}

export type SuggestionUnitKind = "pack" | "lb_case" | "lb" | "each";

export interface RoundedSuggestion {
  /** Whole cases ordered, or null when the line is ordered in loose units/pounds. */
  packs: number | null;
  /** Quantity in the product's unit (eaches or lb). */
  qty: number;
  kind: SuggestionUnitKind;
}

/**
 * Round a positive need to what is ordered (R1 section 12):
 * - weighed with a known average case weight: whole cases, qty = cases x average weight;
 * - weighed otherwise: whole pounds;
 * - a case of 2+: whole cases;
 * - otherwise: whole eaches.
 * A need of zero or less orders nothing (packs 0 / qty 0).
 */
export function roundSuggestion(input: {
  need: number;
  unit: "ea" | "lb";
  packSize: number | null;
  avgCaseWeightLbs: number | null;
}): RoundedSuggestion {
  const { need, unit } = input;
  if (unit === "lb") {
    const avg = input.avgCaseWeightLbs != null && input.avgCaseWeightLbs > 0 ? input.avgCaseWeightLbs : null;
    if (avg) {
      if (!(need > 0)) return { packs: 0, qty: 0, kind: "lb_case" };
      const packs = Math.ceil(need / avg - QTY_EPSILON);
      return { packs, qty: roundQty(packs * avg), kind: "lb_case" };
    }
    return { packs: null, qty: need > 0 ? Math.ceil(need - QTY_EPSILON) : 0, kind: "lb" };
  }
  const k = usablePackSize(input.packSize);
  if (k) {
    const { packs, qty } = ceilToPack(need, k);
    return { packs, qty, kind: "pack" };
  }
  return { packs: null, qty: need > 0 ? Math.ceil(need - QTY_EPSILON) : 0, kind: "each" };
}

/** About how many cases a weight is, given the average shipped case weight; null when unknown. */
export function approxCasesForLb(lb: number, avgCaseWeightLbs: number | null | undefined): number | null {
  if (avgCaseWeightLbs == null || !(avgCaseWeightLbs > 0) || !Number.isFinite(lb)) return null;
  return Math.round((lb / avgCaseWeightLbs) * 10) / 10;
}

/**
 * A quantity in base units (pieces or pounds) as another vendor would take it (TASK-0249, INC-1c line
 * moves): it always goes through single units, never "2 cases" carried across. To a confirmed case size
 * (or one printed on invoices) it rounds up to whole cases; to a case size nobody confirmed, or one an
 * invoice disagrees with, it goes in pieces with `check` so the buyer types the cases; with no case size,
 * whole pieces; a weighed item stays in pounds (about N cases when the case weight is known).
 */
export interface VendorQuantity {
  /** Base units after rounding: pieces, or pounds (3 dp). */
  orderedQty: number;
  /** Whole cases ('pack'), about this many cases ('lb' with a case weight), else null. */
  orderedPacks: number | null;
  /** The case size of a 'pack' line, else null. */
  packSize: number | null;
  kind: "pack" | "each" | "lb";
  /** The vendor's case size is not confirmed: the line is in pieces for the buyer to check. */
  check: boolean;
}

export function quantityForVendor(input: {
  qty: number;
  unit: "ea" | "lb";
  packSize: number | null;
  packStatus: string;
  avgCaseWeightLbs: number | null;
}): VendorQuantity {
  if (input.unit === "lb") {
    const lb = roundQty(input.qty);
    return {
      orderedQty: lb,
      orderedPacks: approxCasesForLb(lb, input.avgCaseWeightLbs),
      packSize: null,
      kind: "lb",
      check: false,
    };
  }
  const k = usablePackSize(input.packSize);
  const each = Math.ceil(input.qty - QTY_EPSILON);
  if (k && (input.packStatus === "confirmed" || input.packStatus === "invoice_only")) {
    const { packs, qty } = ceilToPack(input.qty, k);
    return { orderedQty: qty, orderedPacks: packs, packSize: k, kind: "pack", check: false };
  }
  return { orderedQty: each, orderedPacks: null, packSize: null, kind: "each", check: k !== null };
}

/** One each's share of a case price, rounded to the cent ($10.00 for 24 is 42c). */
export function perEachCents(packCostCents: number, packSize: number): number {
  return Math.round(packCostCents / packSize);
}

/**
 * A line's estimated total. When whole cases are ordered and a case cost exists, it is exactly
 * cases x case cost, so a $10.00 case of 24 stays $10.00 rather than 42c x 24 = $10.08.
 * Otherwise quantity x unit cost, rounded once. Unknown cost gives null, never 0.
 */
export function lineTotalCents(input: {
  qty: number;
  unitCostCents: number | null;
  packCostCents?: number | null;
  packs?: number | null;
}): number | null {
  const { qty, unitCostCents } = input;
  const packCost = input.packCostCents ?? null;
  const packs = input.packs ?? null;
  if (packCost != null && packs != null && Number.isInteger(packs)) return packs * packCost;
  if (unitCostCents == null) return null;
  return Math.round(qty * unitCostCents);
}

export type WaMeNumber = { number: string; reason: null } | { number: null; reason: string };

/**
 * The number a wa.me link needs: digits only, US numbers with the leading 1.
 * 10 digits gets the 1; 11 digits starting with 1 is kept; anything else is refused with the
 * reason shown beside the disabled button.
 */
export function waMeNumber(raw: string | null | undefined): WaMeNumber {
  const digits = (raw ?? "").replace(/\D+/g, "");
  if (digits.length === 0) return { number: null, reason: "No phone number on file for this vendor" };
  if (digits.length === 10) return { number: `1${digits}`, reason: null };
  if (digits.length === 11 && digits.startsWith("1")) return { number: digits, reason: null };
  return {
    number: null,
    reason: `"${raw}" is not a 10-digit US number (it has ${digits.length} digits); WhatsApp needs the area code`,
  };
}

/** The longest order text sent inside a wa.me link; longer text is copied for pasting instead. */
export const WA_ME_TEXT_LIMIT = 1500;

/* ── Invoice numbers (TASK-0232) ─────────────────────────────────────────── */

/** `purchase_orders.invoice_number` is varchar(64); the API and the console refuse anything longer. */
export const INVOICE_NUMBER_MAX_LENGTH = 64;

/** The one sentence shown when an invoice number is over the limit (API refusal and console field). */
export const INVOICE_NUMBER_TOO_LONG_MESSAGE = `Invoice number is too long (${INVOICE_NUMBER_MAX_LENGTH} characters at most)`;

/**
 * An invoice number the way two people would call it the same: ASCII letters and digits only, upper
 * case. "7-0185", "70185 " and "inv 70185" differ only in punctuation and case. The SQL side of the
 * duplicate lookup is `upper(regexp_replace(invoice_number, '[^A-Za-z0-9]', '', 'g'))`, the same rule.
 */
export function normalizeInvoiceNumber(number: string | null | undefined): string {
  return (number ?? "").replace(/[^A-Za-z0-9]/g, "").toUpperCase();
}

/**
 * The key a vendor's invoice is known by: the normalised number. Used by FR-DUP's duplicate warning
 * and by the invoice claim that makes it a hard check later (INC-8a/8b), so both must call this one
 * function.
 *
 * - `overrideDate` (YYYY-MM-DD) gives `<KEY>#<date>`, for a vendor that reuses numbers;
 * - a number with no letters or digits falls back to `SHA:<sha256>` of the file when one is given;
 * - otherwise the key is "" and nothing can be compared (the caller treats it as "no invoice number").
 */
export function invoiceKey(
  number: string | null | undefined,
  opts: { overrideDate?: string; fileSha256?: string } = {},
): string {
  const base = normalizeInvoiceNumber(number);
  if (base === "") return opts.fileSha256 ? `SHA:${opts.fileSha256.toLowerCase()}` : "";
  return opts.overrideDate ? `${base}#${opts.overrideDate}` : base;
}

/* ── Billed lines: one conversion for scanned and typed invoices (TASK-0236, INC-8) ─────────── */

/** Where a billed line's case size came from. Never `products.case_qty`. */
export type BilledPackSource = "printed" | "vendor_link" | "operator";

/**
 * Units of measure an invoice line may print, compared trimmed and upper-cased. Anything outside
 * these three lists (OZ, G, KG, DZ, BX, PK, ...) is `uom_unknown` and is never booked as eaches.
 * A blank or missing uom reads as each.
 */
export const EACH_UOMS: readonly string[] = ["", "EA", "PC", "PCS", "PIECE", "EACH", "UNIT"];
export const CASE_UOMS: readonly string[] = ["CS", "CASE", "CSE", "CA", "CTN"];
export const POUND_UOMS: readonly string[] = ["LB", "LBS", "#"];

export type UomKind = "each" | "case" | "pound" | "unknown";

/** Which of the three lists a printed/typed uom belongs to. */
export function uomKind(uom: string | null | undefined): UomKind {
  const u = (uom ?? "").trim().toUpperCase();
  if (EACH_UOMS.includes(u)) return "each";
  if (CASE_UOMS.includes(u)) return "case";
  if (POUND_UOMS.includes(u)) return "pound";
  return "unknown";
}

export interface BilledLineInput {
  /** The product's procurement unit (`products.unit`). */
  productUnit: "ea" | "lb";
  /** The line's unit: the parse's `unit` for a scan; the product's unit when typed. */
  billedUnit: "ea" | "lb" | "unknown";
  /** Billed quantity in the printed/typed unit of measure. */
  qty: number;
  uom: string | null;
  /** Units per case printed on the line. */
  printedPackSize: number | null;
  /** `vendor_items.units_per_case` for (vendor, product). Never `products.case_qty`. */
  vendorLinkPackSize: number | null;
  /** Typed by the operator; wins over the other two. */
  operatorPackSize?: number | null;
  /** Price per printed/typed uom, integer cents. */
  unitPriceCents: number;
  /** The printed extension, integer cents, when the invoice prints one. */
  lineTotalCents?: number | null;
}

export type BilledLineProblem = "pack_unknown" | "fractional_each" | "uom_unknown" | "unit_mismatch" | "bad_input";

export type BilledLine =
  | {
      ok: true;
      /** Quantity in the product's unit: whole eaches for 'ea', pounds (3 dp) for 'lb'. */
      baseQty: number;
      /** Cases billed when the line is in cases, else null. */
      packs: number | null;
      packSize: number | null;
      packSource: BilledPackSource | null;
      /** Cost per each / per lb in integer cents; null for a zero-quantity (out of stock) line. */
      unitCostCents: number | null;
      /** Cost per case when the line is in cases, else null. */
      packCostCents: number | null;
      lineTotalCents: number;
    }
  | { ok: false; problem: BilledLineProblem };

const isWholeCents = (value: number | null | undefined) => value == null || (Number.isInteger(value) && value >= 0);

/**
 * The only conversion of a billed invoice line to base units and per-unit cents (INC-8, D-23/D-34).
 * The console (typed entry and scan corrections) and the server (recomputed under its lock, with the
 * vendor link read in its transaction) both call it, so a scanned and a typed invoice convert alike.
 *
 * Rules, in order: unknown unit or a unit other than the product's is refused; a uom outside the
 * three lists is refused; a zero quantity is fine with no pack and no cost; a pound product books
 * the quantity as pounds (the uom is informational, the parser puts the weight in qty even when the
 * case count prints as "10 CS"); a case on an each product takes the operator's, else the printed,
 * else the vendor link's pack (never a guess); eaches must come out whole after every conversion.
 */
export function normalizeBilledLine(input: BilledLineInput): BilledLine {
  const { qty, unitPriceCents } = input;
  if (!Number.isFinite(qty) || qty < 0 || !isWholeCents(unitPriceCents) || !isWholeCents(input.lineTotalCents)) {
    return { ok: false, problem: "bad_input" };
  }
  if (input.billedUnit === "unknown") return { ok: false, problem: "uom_unknown" };
  if (input.billedUnit !== input.productUnit) return { ok: false, problem: "unit_mismatch" };
  const kind = uomKind(input.uom);
  if (kind === "unknown") return { ok: false, problem: "uom_unknown" };
  const total = (): number => input.lineTotalCents ?? Math.round(qty * unitPriceCents);

  if (qty === 0) {
    return {
      ok: true,
      baseQty: 0,
      packs: null,
      packSize: null,
      packSource: null,
      unitCostCents: null,
      packCostCents: null,
      lineTotalCents: input.lineTotalCents ?? 0,
    };
  }

  if (input.productUnit === "lb") {
    return {
      ok: true,
      baseQty: roundQty(qty),
      packs: null,
      packSize: null,
      packSource: null,
      unitCostCents: unitPriceCents,
      packCostCents: null,
      lineTotalCents: total(),
    };
  }

  if (kind === "pound") return { ok: false, problem: "unit_mismatch" };

  if (kind === "case") {
    const candidates: [number | null, BilledPackSource][] = [
      [usablePackSize(input.operatorPackSize ?? null), "operator"],
      [usablePackSize(input.printedPackSize), "printed"],
      [usablePackSize(input.vendorLinkPackSize), "vendor_link"],
    ];
    const found = candidates.find(([size]) => size != null);
    if (!found) return { ok: false, problem: "pack_unknown" };
    const packSize = found[0] as number;
    const baseQty = packsToUnits(qty, packSize);
    if (!Number.isInteger(baseQty)) return { ok: false, problem: "fractional_each" };
    return {
      ok: true,
      baseQty,
      packs: qty,
      packSize,
      packSource: found[1],
      unitCostCents: perEachCents(unitPriceCents, packSize),
      packCostCents: unitPriceCents,
      lineTotalCents: total(),
    };
  }

  // each
  const baseQty = roundQty(qty);
  if (!Number.isInteger(baseQty)) return { ok: false, problem: "fractional_each" };
  return {
    ok: true,
    baseQty,
    packs: null,
    packSize: null,
    packSource: null,
    unitCostCents: unitPriceCents,
    packCostCents: null,
    lineTotalCents: total(),
  };
}

/** The parsed-line fields `billedInputFromParsedLine` reads (an `ExtractedLineItem` satisfies it). */
export interface ParsedBilledLine {
  qty: number;
  uom?: string | null;
  unit?: "ea" | "lb" | "unknown";
  unitsPerCase?: number | null;
  /** Dollars. Per each when the pack is known; equal to the case price when it is not. */
  unitPrice: number;
  /** Dollars per case as printed, or null. */
  casePrice?: number | null;
  /** Dollars. */
  lineTotal?: number | null;
}

/** Whole cents from dollars, half-up (the parser speaks dollars). */
function centsOf(dollars: number): number {
  return Math.round(dollars * 100);
}

/**
 * A parsed invoice line as `normalizeBilledLine` input, following the parser contract
 * (`server/api/src/lib/services.ts`, PRICE RULES and UNIT): for a case line the price is the case
 * price; when the parser printed no case price but knew the pack, its `unitPrice` is already per
 * each, so the case price is that times the pack (one rounding), never divided again; with no pack,
 * `unitPrice` was set equal to the case price. A pound line is always priced per pound.
 */
export function billedInputFromParsedLine(
  line: ParsedBilledLine,
  productUnit: "ea" | "lb",
  vendorLinkPackSize: number | null,
): BilledLineInput {
  const billedUnit = line.unit ?? "ea";
  const printedPack = usablePackSize(line.unitsPerCase ?? null);
  let unitPriceCents: number;
  if (billedUnit !== "lb" && uomKind(line.uom) === "case") {
    if (line.casePrice != null) unitPriceCents = centsOf(line.casePrice);
    else if (printedPack != null) unitPriceCents = Math.round(line.unitPrice * printedPack * 100);
    else unitPriceCents = centsOf(line.unitPrice);
  } else {
    unitPriceCents = centsOf(line.unitPrice);
  }
  return {
    productUnit,
    billedUnit,
    qty: line.qty,
    uom: line.uom ?? null,
    printedPackSize: line.unitsPerCase ?? null,
    vendorLinkPackSize,
    unitPriceCents: Math.max(0, unitPriceCents),
    lineTotalCents: line.lineTotal != null && line.lineTotal > 0 ? centsOf(line.lineTotal) : null,
  };
}

/* ── The stock default of one PO line on an invoice (TASK-0236) ────────────────────────────── */

export interface InvoiceLineDefaultInput {
  /** R: the PO line's received_qty now. */
  received: number;
  /** B0: billed on this PO line by live invoices before this one. */
  billedBefore: number;
  /** NR0: of that, marked "did not come" (a credit to chase). */
  notReceivedBefore: number;
  /** A0: received_qty advanced by live invoices before this one. */
  receivedByInvoicesBefore: number;
  /** This request's billed quantity on the PO line (lines sharing a PO line are summed). */
  billedNow: number;
  /** D: counted at the door, max(R - A0, 0) (INC-3 changes how the caller computes it). */
  doorQty: number;
  /** False for a line not on the PO (off order or a substitute): treated as nothing counted at the door. */
  onOrder: boolean;
  receiptDate: string;
  storeToday: string;
  /** Store-local day of the item's last physical count, or null. */
  lastCountedOn: string | null;
  /**
   * TASK-0241 (INC-1b): how the vendor's deliveries are checked in. On a 'door' vendor a line on the order
   * with nothing counted at the door yet is not received silently from the invoice: the operator chooses
   * ("Not counted at the door yet: add from the invoice, or leave for the door count?"). Absent = 'invoice'.
   */
  receivingMode?: "invoice" | "door" | undefined;
}

export type InvoiceLineAction = "receive" | "keep" | "choose";
export type InvoiceLineCountFlag = "none" | "before_count" | "same_day_as_count";

export interface InvoiceLineDefault {
  action: InvoiceLineAction;
  reason: "no_receipt_yet" | "counted_at_door" | "door_count_short" | "not_counted_at_door";
  /** (B0 - NR0) + billedNow: billing accounted as received, plus this invoice. */
  targetReceived: number;
  /** What `receive` adds to received_qty (for `choose`: what "yes, it came" adds). */
  receiveDelta: number;
  /** What `keep` leaves as a credit to chase (for `choose`: what "no, it did not come" leaves). */
  notReceivedQty: number;
  countFlag: InvoiceLineCountFlag;
}

const clampQty = (value: number, min: number, max: number) => roundQty(Math.min(Math.max(value, min), max));

/**
 * The stock default per PO line (INC-8, INC8-C1). The target counts only billing that was accounted
 * as received: quantities an earlier invoice billed but marked "did not come" are a credit to chase,
 * never silently received by a later invoice. Nothing counted at the door: receive up to the target,
 * never more than this invoice bills, never lowering stock. Counted at the door and covered: keep.
 * Counted at the door and short: the operator chooses whether the rest came.
 */
export function invoiceLineDefault(input: InvoiceLineDefaultInput): InvoiceLineDefault {
  const billedNow = Math.max(0, input.billedNow);
  const target = roundQty(input.billedBefore - input.notReceivedBefore + billedNow);
  const gap = roundQty(target - input.received);
  const door = input.onOrder ? input.doorQty : 0;
  const countFlag = (adds: boolean): InvoiceLineCountFlag => {
    if (!adds || input.lastCountedOn == null) return "none";
    if (!(input.receiptDate < input.storeToday) || input.receiptDate > input.lastCountedOn) return "none";
    return input.receiptDate === input.lastCountedOn ? "same_day_as_count" : "before_count";
  };
  if (!(door > 0)) {
    const delta = clampQty(gap, 0, billedNow);
    if (input.receivingMode === "door" && input.onOrder && delta > 0) {
      // "Leave for the door count" receives nothing and leaves no credit to chase: the phone checks it in.
      return {
        action: "choose",
        reason: "not_counted_at_door",
        targetReceived: target,
        receiveDelta: delta,
        notReceivedQty: 0,
        countFlag: countFlag(true),
      };
    }
    return {
      action: "receive",
      reason: "no_receipt_yet",
      targetReceived: target,
      receiveDelta: delta,
      notReceivedQty: billedNow,
      countFlag: countFlag(delta > 0),
    };
  }
  if (!(gap > 0)) {
    return {
      action: "keep",
      reason: "counted_at_door",
      targetReceived: target,
      receiveDelta: 0,
      notReceivedQty: 0,
      countFlag: "none",
    };
  }
  const short = clampQty(gap, 0, billedNow);
  return {
    action: "choose",
    reason: "door_count_short",
    targetReceived: target,
    receiveDelta: short,
    notReceivedQty: short,
    countFlag: countFlag(short > 0),
  };
}

export type InvoiceStockReason = "no_receipt_yet" | "counted_at_door" | "operator_receive" | "operator_not_received";

/**
 * A line group's outcome once the operator's answer is known. `answer` is the Stock answer:
 * `receive` or `keep` ("did not come"); undefined takes the default. A `choose` needs an answer.
 * Returns null when the default is `choose` and there is no answer.
 */
export function invoiceLineOutcome(
  d: InvoiceLineDefault,
  billedNow: number,
  answer: "receive" | "keep" | undefined,
): { action: "receive" | "keep"; receiveDelta: number; notReceivedQty: number; reason: InvoiceStockReason } | null {
  if (d.action === "choose") {
    if (answer === undefined) return null;
    return answer === "receive"
      ? { action: "receive", receiveDelta: d.receiveDelta, notReceivedQty: 0, reason: "operator_receive" }
      : { action: "keep", receiveDelta: 0, notReceivedQty: d.notReceivedQty, reason: "operator_not_received" };
  }
  if (d.action === "keep") {
    return { action: "keep", receiveDelta: 0, notReceivedQty: 0, reason: "counted_at_door" };
  }
  if (answer === "keep") {
    return {
      action: "keep",
      receiveDelta: 0,
      notReceivedQty: roundQty(Math.max(0, billedNow)),
      reason: "operator_not_received",
    };
  }
  return { action: "receive", receiveDelta: d.receiveDelta, notReceivedQty: 0, reason: "no_receipt_yet" };
}

/**
 * Split a group's quantity back over its rows in ascending ref order: each row takes
 * min(its billed quantity, what remains), so the rows sum to the group's amount.
 */
export function allocateByRef<T extends { ref: number; billedQty: number }>(
  rows: T[],
  amount: number,
): { row: T; share: number }[] {
  let remaining = roundQty(Math.max(0, amount));
  return [...rows]
    .sort((a, b) => a.ref - b.ref)
    .map((row) => {
      const share = roundQty(Math.min(Math.max(row.billedQty, 0), remaining));
      remaining = roundQty(remaining - share);
      return { row, share };
    });
}

/**
 * JSON with object keys sorted recursively and arrays in request order; `undefined` members are
 * dropped (as JSON.stringify does). The record route hashes it (with its route and PO) to tell a
 * replay of the same request from a different request reusing a client request id.
 */
export function canonicalRequestJson(value: unknown): string {
  const sortKeys = (v: unknown): unknown => {
    if (Array.isArray(v)) return v.map((item) => (item === undefined ? null : sortKeys(item)));
    if (v !== null && typeof v === "object") {
      const out: Record<string, unknown> = {};
      for (const key of Object.keys(v as Record<string, unknown>).sort()) {
        const member = (v as Record<string, unknown>)[key];
        if (member !== undefined) out[key] = sortKeys(member);
      }
      return out;
    }
    return v;
  };
  return JSON.stringify(sortKeys(value));
}

/* ── The Send request hash (TASK-0241, INC-1b) ─────────────────────────────────────────────── */

/** One guide line as the Send hash sees it. Quantities in base units. */
export interface SendHashLine {
  productId: number;
  unit: "ea" | "lb";
  orderedQty: number;
  orderedPacks?: number | null;
  packSize?: number | null;
  estUnitCostCents?: number | null;
  estPackCostCents?: number | null;
}

export interface SendHashInput {
  vendorId: number;
  expectedOn: string;
  sentVia: string;
  placedBy: string;
  notes?: string | null;
  lines: readonly SendHashLine[];
  /**
   * TASK-0256 (DROP-1): the Send's "Deliver to" (`locationId` null = direct to the store), only when the
   * request carried one. Absent, the input is exactly INC-1b's, so stored hashes and replays are unchanged.
   */
  shipTo?: { locationId: number | null; dropOn: string | null };
}

/**
 * The canonical JSON whose sha256 is a sent PO's `request_hash`: the vendor, expected day, channel, who
 * placed it, the notes, and the lines sorted by product as `[productId, unit, qty (3 dp), packs, packSize,
 * estUnit, estPack]`. A replay with the same `clientRequestId` and this same input is the same Send; any
 * difference is a different request reusing the key.
 *
 * `extraDigest` is for VO-2 (the dispatch digest). Absent, the input is exactly today's, so hashes stored
 * before VO-2 stay comparable.
 */
export function sendRequestHashInput(input: SendHashInput, extraDigest?: string): string {
  const lines = [...input.lines]
    .sort((a, b) => a.productId - b.productId)
    .map((line) => [
      line.productId,
      line.unit,
      roundQty(line.orderedQty).toFixed(3),
      line.orderedPacks ?? null,
      line.packSize ?? null,
      line.estUnitCostCents ?? null,
      line.estPackCostCents ?? null,
    ]);
  const body = {
    vendorId: input.vendorId,
    expectedOn: input.expectedOn,
    sentVia: input.sentVia,
    placedBy: input.placedBy,
    notes: input.notes ?? null,
    lines,
    ...(input.shipTo !== undefined
      ? { shipTo: { locationId: input.shipTo.locationId, dropOn: input.shipTo.dropOn } }
      : {}),
  };
  return canonicalRequestJson(extraDigest === undefined ? body : { ...body, extraDigest });
}

/* ── Close short and the credit to ask (TASK-0251, INC-4) ─────────────────────────────────────── */

/** Why an order was closed short (`purchase_orders.close_reason`, CHECK in migration 0108). */
export const PO_CLOSE_REASONS = ["backorder_cancelled", "out_of_stock", "not_coming", "substituted", "other"] as const;
export type PoCloseReason = (typeof PO_CLOSE_REASONS)[number];

/** The close note's length limit (characters, after trimming). */
export const PO_CLOSE_NOTE_MAX_LENGTH = 500;

/** What an invoice recorded on the order said about the rest of a line (`vendor_invoice_lines.resolution`). */
export type InvoiceLineResolution = "still_coming" | "wont_come" | "substituted";

/**
 * The reason Close short starts on, from what the newest live invoice said about each short line:
 * any line substituted -> `substituted`; every line "won't come" -> `not_coming` (both `fromInvoice`).
 * Otherwise (no invoice, or a line still coming) it starts on `not_coming` and says it is a guess.
 */
export function suggestedCloseReason(resolutions: readonly (InvoiceLineResolution | null | undefined)[]): {
  reason: PoCloseReason;
  fromInvoice: boolean;
} {
  if (resolutions.some((resolution) => resolution === "substituted")) {
    return { reason: "substituted", fromInvoice: true };
  }
  if (resolutions.length > 0 && resolutions.every((resolution) => resolution === "wont_come")) {
    return { reason: "not_coming", fromInvoice: true };
  }
  return { reason: "not_coming", fromInvoice: false };
}

/**
 * The credit still to ask on one PO line (base units): what live invoices billed that never arrived,
 * at most what they marked "did not come". Goods that arrive later (a door receipt after "did not
 * come") settle it: `billed - received` shrinks. A second invoice billing the late goods again keeps
 * the first credit open, because billed grows with it.
 */
export function creditToAskQty(line: { billed: number; notReceived: number; received: number }): number {
  if (!(line.notReceived > 0)) return 0;
  return roundQty(Math.max(0, Math.min(line.notReceived, line.billed - line.received)));
}

/* ── The one cost decision after a receipt (TASK-0252, INC-5) ───────────────────────────────── */

/**
 * A unit cost this many times above (or below) a reference is more likely a case price typed where the unit
 * cost goes (or the reverse) than a real price move: it needs the operator's tick ("yes, this is the unit
 * cost") before it is recorded.
 */
export const UNIT_CASE_RATIO = 3;

/** What a typed cost is compared with for the case-vs-unit check, in this order. */
export type CostReferenceKind = "order_estimate" | "last_paid" | "product_cost";

export interface CostReferences {
  /** The PO line's order estimate (`ordered_unit_cost`). An estimate never becomes a cost; it is only compared. */
  orderEstimateCents: number | null;
  /** What we last paid this vendor (`vendor_items.last_paid_unit_cost_cents`). */
  lastPaidCents: number | null;
  /** The product's cost now (`products.cost`). */
  productCostCents: number | null;
}

export interface UnitCaseSuspect {
  reference: CostReferenceKind;
  referenceCents: number;
  /** cost / reference, one decimal (below 1 when the cost is far under the reference). */
  ratio: number;
}

/** Whether `a` is UNIT_CASE_RATIO times or more away from `b` (above or below); `b` above 0. */
function ratioApart(a: number, b: number): boolean {
  return a >= UNIT_CASE_RATIO * b || a * UNIT_CASE_RATIO <= b;
}

/**
 * The first reference (order estimate, last paid, product cost) the cost is UNIT_CASE_RATIO times or more
 * away from, either way; null when none. A zero or missing reference is skipped, and a zero cost is never
 * suspect (a zero cost never reaches product cost anyway).
 *
 * An order estimate that is itself UNIT_CASE_RATIO times away from the product's cost (above 0) is not a
 * reference: it is most likely a case price typed as the order price (or a copy of a mistyped invoice cost,
 * as Record invoice's draft makes), and comparing with it would ask the operator to confirm the right unit
 * cost as if it were the slip (TASK-0252 review R1b B3). The product cost still catches the slip itself.
 */
export function unitCaseSuspect(costCents: number, refs: CostReferences): UnitCaseSuspect | null {
  if (!(costCents > 0)) return null;
  const product = refs.productCostCents;
  const estimate = refs.orderEstimateCents;
  const estimateTrusted = !(
    estimate != null &&
    estimate > 0 &&
    product != null &&
    product > 0 &&
    ratioApart(estimate, product)
  );
  const ordered: [CostReferenceKind, number | null][] = [
    ["order_estimate", estimateTrusted ? estimate : null],
    ["last_paid", refs.lastPaidCents],
    ["product_cost", product],
  ];
  for (const [reference, cents] of ordered) {
    if (cents == null || !(cents > 0)) continue;
    if (ratioApart(costCents, cents)) {
      return { reference, referenceCents: cents, ratio: Math.round((costCents / cents) * 10) / 10 };
    }
  }
  return null;
}

/**
 * The billed per-unit cost of an invoice recording's rows on one PO line (TASK-0260, INC-9a, D2): lines on one
 * PO line are one unit of arithmetic, so one cost is decided for them: the quantity-weighted mean of the rows'
 * per-unit cents, half-up (two rows at one price: that price); with nothing billed by quantity, the first row's
 * price. Null for a group with no billed price (not stock). The record path decides with it, and a void asks
 * it whether a surviving recording billed a pending row's cost (review R3-3: by its group, not row by row).
 */
export function groupBilledCostCents(rows: { qty: number; unitCostCents: number }[]): number | null {
  if (rows.length === 0) return null;
  let totalQty = 0;
  let totalCost = 0;
  for (const row of rows) {
    totalQty += row.qty;
    totalCost += row.qty * row.unitCostCents;
  }
  if (!(totalQty > 0)) return rows[0]!.unitCostCents;
  return cents(totalCost / totalQty);
}

/** |new - old| / old x 100, the move the approval threshold is compared with (old above 0). */
export function costChangePct(oldCents: number, newCents: number): number {
  return Math.abs((newCents - oldCents) / oldCents) * 100;
}

export type LineCostDecisionKind = "applied" | "pending" | "unchanged" | "needs_ack" | "none";

/**
 * Why the decision came out as it did:
 *  - no_cost: no cost is known (none typed, none on the line);
 *  - not_received: nothing is received on the line yet, so its cost is an order line's, not a receipt's;
 *  - zero_cost: a zero cost is recorded on the line but never touches product cost;
 *  - same_as_pending: the line's open cost change already asks for exactly this cost (typed again);
 *  - rejected: this is the line's own cost and the line's newest cost change, a rejection, refused it: it is
 *    not decided again (no new approval row, no product cost, no last paid); a different cost is;
 *  - same_as_product: the product already costs this;
 *  - unit_case: 3x or more away from a reference and not ticked;
 *  - newer_receipt: a newer receipt of the product already holds a cost (decided, or waiting for approval),
 *    so this older one is recorded on its line only (one at or above the threshold is held for review behind
 *    the newer one: `overThreshold`);
 *  - first_cost: the product had no cost (0 or none): it applies with no approval;
 *  - within_threshold / above_threshold: the move against the product's cost.
 */
export type LineCostReason =
  | "no_cost"
  | "not_received"
  | "zero_cost"
  | "same_as_pending"
  | "rejected"
  | "same_as_product"
  | "unit_case"
  | "newer_receipt"
  | "first_cost"
  | "within_threshold"
  | "above_threshold";

export interface LineCostFacts {
  /** The cost being decided: the one typed, else the line's own unit_cost. */
  costCents: number | null;
  productCostCents: number | null;
  /** settings `cost_change_approval_threshold_pct`. */
  thresholdPct: number;
  /** new_cost of the line's open (not superseded) pending cost change, if any. */
  openPendingNewCostCents: number | null;
  /**
   * The cost being decided is the line's own cost, and the line's newest cost change (since its last reverse)
   * is a rejection of it (TASK-0252 review F1).
   */
  ownCostRejected: boolean;
  /** A newer receipt of the same product already holds a cost. */
  newerReceipt: boolean;
  /** Whether the case-vs-unit check applies to this cost (a cost new to the line). */
  checkUnitCase: boolean;
  ackUnitCase: boolean;
  references: CostReferences;
}

/**
 * One line's cost decision as the API's responses carry it (the receive response's `costDecisions`, cost
 * entry's `decisions`, and since INC-9a inbox confirm's and the invoice recording's `costDecisions`); built by
 * `lineCostView` in server/api/src/lib/receive-cost.ts. Additive fields only.
 */
export interface LineCostDecisionView {
  itemId: number;
  decision: LineCostDecisionKind | "deferred";
  reason: LineCostReason;
  /** The line's cost after the decision (NULL when deferred). */
  unitCostCents: number | null;
  /** The cost typed (or billed) when it needs the tick or was deferred. */
  typedUnitCostCents?: number;
  reference?: CostReferenceKind;
  referenceCents?: number;
  pendingCostChangeId?: number;
  /** An older delivery's cost at or above the threshold, held for review behind a newer one. */
  heldCostChangeId?: number;
  supersededIds?: number[];
  /** deferred: the line kept the confirmed cost it already held (the typed one was not used). */
  keptUnitCostCents?: number;
  /** INC-9a: an invoice's older receipt kept as history (counted in the average; product cost untouched). */
  history?: true;
  productCostBefore: number | null;
  productCostAfter: number | null;
  /** false: the vendor is not linked to the product, so its last paid was not kept. */
  vendorLinked?: false;
}

export interface LineCostDecision {
  decision: LineCostDecisionKind;
  reason: LineCostReason;
  suspect: UnitCaseSuspect | null;
  /** The move against the product's cost, rounded to 2 decimals; null without a product cost above 0. */
  pctChange: number | null;
  /** The move against the product's cost (above 0) is at or above the approval threshold. */
  overThreshold: boolean;
}

/**
 * The one cost decision (lib/receive-cost.ts `applyLineCostInTx` applies it under its locks; the console
 * previews it). It never looks at the quantity received: a cost entered after the door count decides the
 * same way as one typed at the door.
 */
export function decideLineCost(facts: LineCostFacts): LineCostDecision {
  const cost = facts.costCents;
  const product = facts.productCostCents;
  const pct =
    cost != null && product != null && product > 0 ? Math.round(costChangePct(product, cost) * 100) / 100 : null;
  const overThreshold =
    cost != null && cost > 0 && product != null && product > 0 && costChangePct(product, cost) >= facts.thresholdPct;
  const out = (decision: LineCostDecisionKind, reason: LineCostReason, suspect: UnitCaseSuspect | null = null) => ({
    decision,
    reason,
    suspect,
    pctChange: pct,
    overThreshold,
  });
  if (cost == null) return out("none", "no_cost");
  if (cost === 0) return out("unchanged", "zero_cost");
  if (facts.openPendingNewCostCents != null && facts.openPendingNewCostCents === cost) {
    return out("unchanged", "same_as_pending");
  }
  if (facts.ownCostRejected) return out("unchanged", "rejected");
  if (product != null && product === cost) return out("unchanged", "same_as_product");
  if (facts.checkUnitCase && !facts.ackUnitCase) {
    const suspect = unitCaseSuspect(cost, facts.references);
    if (suspect) return out("needs_ack", "unit_case", suspect);
  }
  if (facts.newerReceipt) return out("unchanged", "newer_receipt");
  if (product == null || product <= 0) return out("applied", "first_cost");
  return overThreshold ? out("pending", "above_threshold") : out("applied", "within_threshold");
}
